(() => {
  // src/core/constants.js
  var MILLIMETERS_PER_UNIT = Object.freeze({
    millimeter: 1,
    centimeter: 10,
    meter: 1e3,
    inch: 25.4,
    foot: 304.8,
    stud: 8,
    brick: 9.6,
    plate: 3.2,
    ldu: 0.4,
    baseplate32: 256
  });
  var SUPPORTED_UNITS = Object.freeze(
    Object.keys(MILLIMETERS_PER_UNIT)
  );

  // src/core/conversions.js
  function assertSupportedUnit(unit) {
    if (!Object.hasOwn(MILLIMETERS_PER_UNIT, unit)) {
      throw new RangeError(`Unsupported unit: ${String(unit)}`);
    }
  }
  function assertMeasurement(value, name = "value") {
    if (!Number.isFinite(value)) {
      throw new TypeError(`${name} must be finite.`);
    }
    if (value < 0) {
      throw new RangeError(`${name} must not be negative.`);
    }
  }
  function assertFiniteResult(value) {
    if (!Number.isFinite(value)) {
      throw new RangeError("Converted result must be finite.");
    }
    return value;
  }
  function toMillimeters(value, unit) {
    assertMeasurement(value);
    assertSupportedUnit(unit);
    return assertFiniteResult(value * MILLIMETERS_PER_UNIT[unit]);
  }
  function fromMillimeters(value, unit) {
    assertMeasurement(value);
    assertSupportedUnit(unit);
    return assertFiniteResult(value / MILLIMETERS_PER_UNIT[unit]);
  }
  function convertToAllUnits(value, sourceUnit) {
    const millimeters = toMillimeters(value, sourceUnit);
    return Object.fromEntries(
      SUPPORTED_UNITS.map((unit) => [
        unit,
        fromMillimeters(millimeters, unit)
      ])
    );
  }

  // src/core/validation.js
  var STRICT_DECIMAL_PATTERN = /^(?:\d+(?:[.,]\d+)?|[.,]\d+)$/;
  var NON_FINITE_PATTERN = /^(?:nan|[+-]?infinity)$/i;
  function invalid(raw, code) {
    return Object.freeze({ valid: false, raw, code });
  }
  function parseMeasurement(rawInput) {
    const raw = String(rawInput ?? "");
    const trimmed = raw.trim();
    if (trimmed === "") {
      return invalid(raw, "empty");
    }
    if (NON_FINITE_PATTERN.test(trimmed)) {
      return invalid(raw, "non_finite");
    }
    if (trimmed.includes(".") && trimmed.includes(",")) {
      return invalid(raw, "mixed_separators");
    }
    if (trimmed.startsWith("-")) {
      return invalid(raw, "negative");
    }
    if (!STRICT_DECIMAL_PATTERN.test(trimmed)) {
      return invalid(raw, "malformed");
    }
    const normalized = trimmed.replace(",", ".");
    const value = Number(normalized);
    if (!Number.isFinite(value)) {
      return invalid(raw, "non_finite");
    }
    if (value < 0) {
      return invalid(raw, "negative");
    }
    return Object.freeze({ valid: true, raw, normalized, value });
  }

  // src/core/scale.js
  function validateScaleDenominator(rawValue) {
    if (typeof rawValue === "number") {
      if (!Number.isFinite(rawValue)) {
        return Object.freeze({ valid: false, raw: rawValue, code: "non_finite" });
      }
      if (rawValue < 1) {
        return Object.freeze({ valid: false, raw: rawValue, code: "scale_too_small" });
      }
      return Object.freeze({
        valid: true,
        raw: rawValue,
        normalized: String(rawValue),
        value: rawValue,
        denominator: rawValue
      });
    }
    const parsed = parseMeasurement(rawValue);
    if (!parsed.valid) return parsed;
    if (parsed.value < 1) {
      return Object.freeze({
        valid: false,
        raw: parsed.raw,
        code: "scale_too_small"
      });
    }
    return Object.freeze({ ...parsed, denominator: parsed.value });
  }
  function assertDenominator(denominator) {
    if (!Number.isFinite(denominator)) {
      throw new TypeError("denominator must be finite.");
    }
    if (denominator < 1) {
      throw new RangeError("denominator must be at least 1.");
    }
  }
  function calculateScaledMillimeters(originalMillimeters, denominator) {
    assertMeasurement(originalMillimeters, "originalMillimeters");
    assertDenominator(denominator);
    const result = originalMillimeters / denominator;
    if (!Number.isFinite(result)) throw new RangeError("Scaled result must be finite.");
    return result;
  }
  function calculateOriginalMillimeters(scaledMillimeters, denominator) {
    assertMeasurement(scaledMillimeters, "scaledMillimeters");
    assertDenominator(denominator);
    const result = scaledMillimeters * denominator;
    if (!Number.isFinite(result)) throw new RangeError("Original result must be finite.");
    return result;
  }
  function calculateScalePair({
    value,
    unit,
    sourceSide,
    denominator
  } = {}) {
    assertMeasurement(value);
    assertSupportedUnit(unit);
    assertDenominator(denominator);
    if (!["original", "scaled"].includes(sourceSide)) {
      throw new RangeError(`Unsupported source side: ${String(sourceSide)}`);
    }
    const sourceMillimeters = toMillimeters(value, unit);
    if (!Number.isFinite(sourceMillimeters)) {
      throw new RangeError("Source conversion must be finite.");
    }
    const originalMillimeters = sourceSide === "original" ? sourceMillimeters : calculateOriginalMillimeters(sourceMillimeters, denominator);
    const scaledMillimeters = sourceSide === "scaled" ? sourceMillimeters : calculateScaledMillimeters(sourceMillimeters, denominator);
    return Object.freeze({
      denominator,
      sourceSide,
      sourceUnit: unit,
      sourceValue: value,
      originalMillimeters,
      scaledMillimeters,
      originalValues: Object.freeze(convertToAllUnits(
        fromMillimeters(originalMillimeters, unit),
        unit
      )),
      scaledValues: Object.freeze(convertToAllUnits(
        fromMillimeters(scaledMillimeters, unit),
        unit
      ))
    });
  }

  // src/presentation/formatting.js
  function formatNumber(value, maximumFractionDigits = 3) {
    if (!Number.isFinite(value)) {
      throw new TypeError("value must be finite.");
    }
    const normalized = Object.is(value, -0) ? 0 : value;
    return new Intl.NumberFormat("en-US", {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits
    }).format(normalized);
  }
  function formatSignedDifference(value, unit = "mm") {
    if (!Number.isFinite(value)) {
      throw new TypeError("value must be finite.");
    }
    const normalized = Math.abs(value) < 1e-9 ? 0 : value;
    const sign = normalized > 0 ? "+" : normalized < 0 ? "\u2212" : "";
    return `${sign}${formatNumber(Math.abs(normalized))} ${unit}`;
  }
  function formatDimensions(values, unit) {
    return `${formatNumber(values.length)} \xD7 ${formatNumber(values.width)} \xD7 ${formatNumber(values.height)} ${unit}`;
  }
  function formatCount(value, singular, plural = `${singular}s`) {
    return `${formatNumber(value)} ${value === 1 ? singular : plural}`;
  }
  var BUILDING_UNIT_LABELS = Object.freeze({
    stud: Object.freeze(["stud", "studs"]),
    brick: Object.freeze(["brick", "bricks"]),
    plate: Object.freeze(["plate", "plates"]),
    ldu: Object.freeze(["LDraw unit", "LDraw units"]),
    baseplate32: Object.freeze(["32\xD732 baseplate", "32\xD732 baseplates"])
  });
  var ABBREVIATED_UNIT_LABELS = Object.freeze({
    millimeter: "mm",
    centimeter: "cm",
    meter: "m",
    inch: "in",
    foot: "ft"
  });
  function formatUnitValue(value, unit) {
    if (Object.hasOwn(BUILDING_UNIT_LABELS, unit)) {
      return formatCount(value, ...BUILDING_UNIT_LABELS[unit]);
    }
    if (Object.hasOwn(ABBREVIATED_UNIT_LABELS, unit)) {
      return `${formatNumber(value)} ${ABBREVIATED_UNIT_LABELS[unit]}`;
    }
    throw new RangeError(`Unsupported presentation unit: ${String(unit)}`);
  }
  function formatBuildableHeight(bricks, plates) {
    const parts = [];
    if (bricks > 0) parts.push(formatUnitValue(bricks, "brick"));
    if (plates > 0) parts.push(formatUnitValue(plates, "plate"));
    return parts.length > 0 ? parts.join(" + ") : formatUnitValue(0, "plate");
  }
  function formatScaleRatio(denominator) {
    return `1:${formatNumber(denominator)}`;
  }

  // src/presentation/copy-summary.js
  function originalDimension(dimension) {
    return formatUnitValue(dimension.value, dimension.unit);
  }
  function createCopySummary(input, result) {
    const buildable = result.height.buildable;
    const length = result.closestStuds.length;
    const width = result.closestStuds.width;
    const grid = result.baseplateGrid;
    return [
      "HTBI Size Calculator results",
      `Original input: ${originalDimension(input.length)} \xD7 ${originalDimension(input.width)} \xD7 ${originalDimension(input.height)}`,
      "",
      "Target size",
      `Target metric dimensions: ${formatDimensions(result.centimeters, "cm")}`,
      `Target imperial dimensions: ${formatDimensions(result.inches, "in")}`,
      `Exact stud footprint: ${formatNumber(result.studFootprint.length)} \xD7 ${formatNumber(result.studFootprint.width)} studs`,
      "",
      "Closest buildable size",
      `Recommended whole-stud footprint: ${length.roundedStuds} \xD7 ${width.roundedStuds} studs`,
      `Actual physical footprint: ${formatNumber(fromMillimeters(length.actualMillimeters, "centimeter"))} \xD7 ${formatNumber(fromMillimeters(width.actualMillimeters, "centimeter"))} cm`,
      `Length difference: ${formatSignedDifference(length.differenceMillimeters)}`,
      `Width difference: ${formatSignedDifference(width.differenceMillimeters)}`,
      `Buildable height: ${formatBuildableHeight(buildable.bricks, buildable.remainingPlates)}`,
      `Actual height: ${formatNumber(fromMillimeters(buildable.actualMillimeters, "centimeter"))} cm`,
      `Height difference: ${formatSignedDifference(buildable.differenceMillimeters)}`,
      "",
      "Minimum 32\xD732 baseplate coverage",
      `Along length: ${grid.lengthBaseplates}`,
      `Along width: ${grid.widthBaseplates}`,
      `Total baseplates: ${formatUnitValue(grid.total, "baseplate32")}`,
      `Coverage dimensions: ${formatNumber(grid.coveredLengthCentimeters)} \xD7 ${formatNumber(grid.coveredWidthCentimeters)} cm`,
      "This is the minimum rectangular baseplate area needed to contain the footprint. The model does not have to be built on baseplates."
    ].join("\n");
  }

  // src/ui/state.js
  var DEFAULT_MODEL_UNITS = Object.freeze({
    length: "stud",
    width: "stud",
    height: "brick"
  });
  function createInitialState() {
    return {
      mode: "quick",
      quick: {
        sourceUnit: null,
        rawValue: "",
        canonicalMillimeters: null,
        values: {},
        error: null
      },
      model: {
        fields: {
          length: { rawValue: "", unit: DEFAULT_MODEL_UNITS.length, error: null },
          width: { rawValue: "", unit: DEFAULT_MODEL_UNITS.width, error: null },
          height: { rawValue: "", unit: DEFAULT_MODEL_UNITS.height, error: null }
        },
        result: null
      },
      scale: {
        rawDenominator: "35",
        lastValidDenominator: 35,
        activeSourceSide: null,
        activeSourceUnit: null,
        rawSourceValue: "",
        canonicalOriginalMillimeters: null,
        canonicalScaledMillimeters: null,
        values: { original: {}, scaled: {} },
        denominatorError: null,
        sourceError: null
      },
      copyStatus: "idle"
    };
  }
  function withMode(state, mode) {
    if (!["quick", "model", "scale"].includes(mode)) {
      throw new RangeError(`Unsupported mode: ${mode}`);
    }
    return { ...state, mode, copyStatus: "idle" };
  }
  function withQuickSource(state, update) {
    return {
      ...state,
      quick: { ...state.quick, ...update }
    };
  }
  function clearQuick(state) {
    return {
      ...state,
      quick: createInitialState().quick
    };
  }
  function withModelField(state, fieldName, update) {
    if (!Object.hasOwn(state.model.fields, fieldName)) {
      throw new RangeError(`Unsupported model field: ${fieldName}`);
    }
    return {
      ...state,
      model: {
        ...state.model,
        fields: {
          ...state.model.fields,
          [fieldName]: { ...state.model.fields[fieldName], ...update }
        }
      },
      copyStatus: "idle"
    };
  }
  function withModelResult(state, result) {
    return { ...state, model: { ...state.model, result } };
  }
  function withScaleState(state, update) {
    return {
      ...state,
      scale: {
        ...state.scale,
        ...update,
        values: update.values ? {
          original: { ...update.values.original },
          scaled: { ...update.values.scaled }
        } : state.scale.values
      }
    };
  }
  function clearScale(state) {
    const cleared = createInitialState().scale;
    return {
      ...state,
      scale: {
        ...cleared,
        rawDenominator: String(state.scale.lastValidDenominator),
        lastValidDenominator: state.scale.lastValidDenominator
      }
    };
  }
  function withCopyStatus(state, copyStatus) {
    return { ...state, copyStatus };
  }

  // src/core/model-dimensions.js
  var STUDS_PER_BASEPLATE_SIDE = MILLIMETERS_PER_UNIT.baseplate32 / MILLIMETERS_PER_UNIT.stud;
  function calculateBaseplateGrid(lengthStuds, widthStuds) {
    assertMeasurement(lengthStuds, "lengthStuds");
    assertMeasurement(widthStuds, "widthStuds");
    const lengthBaseplates = Math.ceil(lengthStuds / STUDS_PER_BASEPLATE_SIDE);
    const widthBaseplates = Math.ceil(widthStuds / STUDS_PER_BASEPLATE_SIDE);
    const coveredLengthStuds = lengthBaseplates * STUDS_PER_BASEPLATE_SIDE;
    const coveredWidthStuds = widthBaseplates * STUDS_PER_BASEPLATE_SIDE;
    const coveredLengthMillimeters = lengthBaseplates * MILLIMETERS_PER_UNIT.baseplate32;
    const coveredWidthMillimeters = widthBaseplates * MILLIMETERS_PER_UNIT.baseplate32;
    return Object.freeze({
      lengthBaseplates,
      widthBaseplates,
      columns: lengthBaseplates,
      rows: widthBaseplates,
      total: lengthBaseplates * widthBaseplates,
      coveredLengthStuds,
      coveredWidthStuds,
      coveredLengthMillimeters,
      coveredWidthMillimeters,
      coveredLengthCentimeters: fromMillimeters(
        coveredLengthMillimeters,
        "centimeter"
      ),
      coveredWidthCentimeters: fromMillimeters(
        coveredWidthMillimeters,
        "centimeter"
      )
    });
  }
  function calculateClosestStudSize(targetMillimeters) {
    assertMeasurement(targetMillimeters, "targetMillimeters");
    const exactStuds = targetMillimeters / 8;
    const roundedStuds = Math.round(exactStuds);
    const actualMillimeters = roundedStuds * 8;
    return Object.freeze({
      exactStuds,
      roundedStuds,
      actualMillimeters,
      differenceMillimeters: actualMillimeters - targetMillimeters
    });
  }
  function calculateClosestBuildableHeight(targetMillimeters) {
    assertMeasurement(targetMillimeters, "targetMillimeters");
    const exactPlates = targetMillimeters / 3.2;
    const roundedPlates = Math.round(exactPlates);
    const bricks = Math.floor(roundedPlates / 3);
    const remainingPlates = roundedPlates % 3;
    const actualMillimeters = roundedPlates * 3.2;
    return Object.freeze({
      exactPlates,
      roundedPlates,
      bricks,
      remainingPlates,
      actualMillimeters,
      differenceMillimeters: actualMillimeters - targetMillimeters
    });
  }
  function toDimensionMillimeters(dimension, name) {
    if (!dimension || typeof dimension !== "object") {
      throw new TypeError(`${name} must be a dimension object.`);
    }
    return toMillimeters(dimension.value, dimension.unit);
  }
  function calculateModelDimensions(input) {
    if (!input || typeof input !== "object") {
      throw new TypeError("input must be an object.");
    }
    const lengthMillimeters = toDimensionMillimeters(input.length, "length");
    const widthMillimeters = toDimensionMillimeters(input.width, "width");
    const heightMillimeters = toDimensionMillimeters(input.height, "height");
    const lengthStuds = fromMillimeters(lengthMillimeters, "stud");
    const widthStuds = fromMillimeters(widthMillimeters, "stud");
    return Object.freeze({
      input: Object.freeze({
        length: Object.freeze({ ...input.length }),
        width: Object.freeze({ ...input.width }),
        height: Object.freeze({ ...input.height })
      }),
      millimeters: Object.freeze({
        length: lengthMillimeters,
        width: widthMillimeters,
        height: heightMillimeters
      }),
      studFootprint: Object.freeze({ length: lengthStuds, width: widthStuds }),
      centimeters: Object.freeze({
        length: fromMillimeters(lengthMillimeters, "centimeter"),
        width: fromMillimeters(widthMillimeters, "centimeter"),
        height: fromMillimeters(heightMillimeters, "centimeter")
      }),
      inches: Object.freeze({
        length: fromMillimeters(lengthMillimeters, "inch"),
        width: fromMillimeters(widthMillimeters, "inch"),
        height: fromMillimeters(heightMillimeters, "inch")
      }),
      height: Object.freeze({
        exactBricks: fromMillimeters(heightMillimeters, "brick"),
        exactPlates: fromMillimeters(heightMillimeters, "plate"),
        buildable: calculateClosestBuildableHeight(heightMillimeters)
      }),
      closestStuds: Object.freeze({
        length: calculateClosestStudSize(lengthMillimeters),
        width: calculateClosestStudSize(widthMillimeters)
      }),
      baseplateGrid: calculateBaseplateGrid(lengthStuds, widthStuds)
    });
  }

  // src/ui/calculation-status.js
  function controlledCalculation(calculate) {
    try {
      return Object.freeze({ valid: true, value: calculate(), error: null });
    } catch (error) {
      if (!(error instanceof RangeError || error instanceof TypeError)) throw error;
      return Object.freeze({ valid: false, value: null, error: "out_of_range" });
    }
  }
  function calculateQuickStatus(value, unit) {
    return controlledCalculation(() => Object.freeze({
      canonicalMillimeters: toMillimeters(value, unit),
      values: Object.freeze(convertToAllUnits(value, unit))
    }));
  }
  function calculateModelStatus(input) {
    return controlledCalculation(() => calculateModelDimensions(input));
  }
  function calculateScaleStatus(input) {
    return controlledCalculation(() => calculateScalePair(input));
  }

  // src/ui/status-lifecycle.js
  function createAnnouncementController({
    write,
    delay = 350,
    setTimer = setTimeout,
    clearTimer = clearTimeout
  }) {
    let timer = null;
    function cancel({ clear = true } = {}) {
      if (timer !== null) clearTimer(timer);
      timer = null;
      if (clear) write("");
    }
    function schedule(message) {
      cancel({ clear: false });
      timer = setTimer(() => {
        timer = null;
        write(message);
      }, delay);
    }
    function replace(message) {
      cancel({ clear: false });
      write(message);
    }
    return Object.freeze({ cancel, replace, schedule });
  }
  function createCopyStatusController({ onChange }) {
    let status = "idle";
    function set(nextStatus) {
      if (!["idle", "success", "error"].includes(nextStatus)) {
        throw new RangeError(`Unsupported copy status: ${String(nextStatus)}`);
      }
      status = nextStatus;
      onChange(status);
    }
    return Object.freeze({
      clear: () => set("idle"),
      getStatus: () => status,
      set
    });
  }

  // src/ui/ui-text.js
  var UI_TEXT = Object.freeze({
    title: "HTBI Size Calculator",
    eyebrow: "Model planning tool",
    introduction: "Convert building-system units, plan buildable model dimensions, and translate linear measurements to scale.",
    quickMode: "Quick Converter",
    modelMode: "Model Dimensions",
    scaleMode: "Scale Converter",
    modeSelectorLabel: "Calculator mode",
    quickInstructions: "Edit any field. That field becomes the source for every conversion.",
    modelIntroduction: "Enter the target length, width and height of your model. The calculator converts them into building units, suggests the closest size that can be built with whole studs and plates, and estimates the minimum 32\xD732 baseplate area needed to contain the footprint.",
    modelFields: Object.freeze({ length: "Length", width: "Width", height: "Height" }),
    unitControl: (fieldLabel) => `${fieldLabel} unit`,
    selectedScale: "Selected scale",
    originalSize: "Original Size",
    scaledSize: "Scaled Size",
    originalRatio: "1:1",
    ratioPrefix: "1 :",
    sourceOriginal: "Source value \u2014 Original Size",
    sourceScaled: "Source value \u2014 Scaled Size",
    scaleIntroduction: "Convert a linear measurement between its original 1:1 size and a selected reduced scale.",
    scaleComparisonLabel: "Original and scaled measurements",
    scaleUnitHeading: "Unit",
    scaleExplanation: "Scale calculations use linear dimensions. At 1:35, the scaled measurement is the original measurement divided by 35.",
    scaleFieldLabel: (sideLabel, ratio, unitLabel) => `${sideLabel} ${ratio} \u2014 ${unitLabel}`,
    scaleSourceLabel: (sourceLabel, unitLabel) => `${sourceLabel} \u2014 ${unitLabel}`,
    targetSize: "Target size",
    closestBuildableSize: "Closest buildable size",
    baseplateCoverage: "Minimum 32\xD732 baseplate coverage",
    moreDetails: "More details",
    lengthDirection: "along length",
    widthDirection: "along width",
    coverageArea: "Coverage area",
    totalSuffix: "total",
    coverageExplanation: "This is the minimum rectangular baseplate area needed to contain the footprint. The model does not have to be built on baseplates.",
    resultLabels: Object.freeze({
      targetDimensions: "Target dimensions",
      imperialDimensions: "Imperial dimensions",
      exactFootprint: "Exact footprint",
      recommendedFootprint: "Recommended footprint",
      actualFootprint: "Actual footprint",
      footprintDifference: "Footprint difference",
      buildableHeight: "Buildable height",
      actualHeight: "Actual height",
      heightDifference: "Height difference",
      exactHeightBricks: "Exact height in bricks",
      exactHeightPlates: "Exact height in plates",
      roundedWholePlates: "Rounded whole plates",
      actualRoundedLength: "Actual rounded length",
      actualRoundedWidth: "Actual rounded width",
      lengthDifference: "Length difference",
      widthDifference: "Width difference",
      coveredLength: "Covered length",
      coveredWidth: "Covered width",
      technicalBaseplateGrid: "Technical baseplate grid"
    }),
    clear: "Clear",
    copyResults: "Copy Results",
    source: "Source",
    referenceTitle: "Reference dimensions",
    referenceItems: Object.freeze([
      Object.freeze({ label: "1 stud", value: "8 mm" }),
      Object.freeze({ label: "1 plate", value: "3.2 mm" }),
      Object.freeze({ label: "1 brick", value: "9.6 mm" })
    ]),
    howTitle: "How it works",
    howText: "Conversions use millimeters as the canonical value. Horizontal sizes align to studs, while buildable heights align to whole plates.",
    measurementDisclaimer: "Measurements are based on nominal building-system dimensions. Actual model size may vary because of overhangs, angled elements and individual part geometry.",
    fanDisclaimer: "Unofficial fan-made tool. It is not affiliated with or endorsed by any building-system manufacturer.",
    javascriptRequired: "JavaScript is required for interactive calculations.",
    errors: Object.freeze({
      empty: "Enter a value.",
      negative: "Use zero or a positive value.",
      mixed_separators: "Use either a decimal point or decimal comma, not both.",
      non_finite: "Enter a finite number.",
      malformed: "Enter a valid number, such as 12, 12.5, or 12,5.",
      scale_too_small: "Enter a scale denominator of 1 or greater.",
      out_of_range: "This value produces a result outside the supported numeric range."
    }),
    copySuccess: "Results copied to the clipboard.",
    copyError: "Results could not be copied. Check clipboard permissions and try again.",
    units: Object.freeze({ studs: "studs", centimeter: "cm", inch: "in" }),
    announcements: Object.freeze({
      convertedFrom: (unitLabel) => `Converted from ${unitLabel}.`,
      modelUpdated: "Model dimensions updated.",
      scaleUpdated: (ratio) => `Scale results updated for ${ratio}.`,
      quickCleared: "Quick Converter cleared.",
      scaleCleared: "Scale Converter cleared."
    })
  });
  var UNIT_LABELS = Object.freeze({
    stud: "Studs",
    brick: "Bricks",
    plate: "Plates",
    ldu: "LDraw Units",
    baseplate32: "32\xD732 Baseplates",
    millimeter: "Millimeters",
    centimeter: "Centimeters",
    meter: "Meters",
    inch: "Inches",
    foot: "Feet"
  });
  var QUICK_UNIT_GROUPS = Object.freeze([
    Object.freeze({ label: "Building units", units: Object.freeze(["stud", "brick", "plate", "ldu", "baseplate32"]) }),
    Object.freeze({ label: "Metric units", units: Object.freeze(["millimeter", "centimeter", "meter"]) }),
    Object.freeze({ label: "Imperial units", units: Object.freeze(["inch", "foot"]) })
  ]);
  var SCALE_UNITS = Object.freeze(
    QUICK_UNIT_GROUPS.flatMap(({ units }) => units)
  );
  var MODEL_UNITS = Object.freeze({
    length: Object.freeze(["stud", "millimeter", "centimeter", "meter", "inch", "foot", "baseplate32"]),
    width: Object.freeze(["stud", "millimeter", "centimeter", "meter", "inch", "foot", "baseplate32"]),
    height: Object.freeze(["brick", "plate", "millimeter", "centimeter", "meter", "inch", "foot", "ldu"])
  });

  // src/ui/calculator.js
  var MODEL_FIELDS = Object.freeze(["length", "width", "height"]);
  var SCALE_SIDES = Object.freeze(["original", "scaled"]);
  var SCALE_PRESETS = Object.freeze([10, 12, 20, 24, 32, 35, 43, 48, 50, 64, 72, 87, 100]);
  function createInstanceKey() {
    if (globalThis.crypto?.randomUUID) {
      return `htbi-size-${globalThis.crypto.randomUUID()}`;
    }
    return `htbi-size-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  function quickFieldsMarkup(instanceKey) {
    return QUICK_UNIT_GROUPS.map(({ label, units }) => `
    <fieldset class="htbi-size-unit-group">
      <legend class="htbi-size-unit-legend">${label}</legend>
      <div class="htbi-size-unit-list">
        ${units.map((unit) => {
      const id = `${instanceKey}-quick-${unit}`;
      return `
            <div class="htbi-size-field htbi-size-quick-field" data-quick-row="${unit}">
              <div class="htbi-size-label-line">
                <label class="htbi-size-label" for="${id}">${UNIT_LABELS[unit]}</label>
                <span class="htbi-size-source-badge" data-source-badge hidden>${UI_TEXT.source}</span>
              </div>
              <input class="htbi-size-input" id="${id}" type="text" inputmode="decimal" autocomplete="off" data-quick-unit="${unit}" aria-describedby="${id}-error">
              <span class="htbi-size-error" id="${id}-error" data-quick-error="${unit}"></span>
            </div>`;
    }).join("")}
      </div>
    </fieldset>`).join("");
  }
  function unitOptions(fieldName, selected) {
    return MODEL_UNITS[fieldName].map((unit) => `<option value="${unit}"${unit === selected ? " selected" : ""}>${UNIT_LABELS[unit]}</option>`).join("");
  }
  function modelFieldMarkup(instanceKey, fieldName, label, selectedUnit) {
    const inputId = `${instanceKey}-${fieldName}`;
    const unitId = `${instanceKey}-${fieldName}-unit`;
    return `
    <div class="htbi-size-field htbi-size-dimension-field">
      <label class="htbi-size-label" for="${inputId}">${label}</label>
      <div class="htbi-size-input-pair">
        <input class="htbi-size-input" id="${inputId}" type="text" inputmode="decimal" autocomplete="off" data-model-field="${fieldName}" aria-describedby="${inputId}-error">
        <label class="htbi-size-visually-hidden" for="${unitId}">${UI_TEXT.unitControl(label)}</label>
        <select class="htbi-size-select" id="${unitId}" data-model-unit="${fieldName}">
          ${unitOptions(fieldName, selectedUnit)}
        </select>
      </div>
      <span class="htbi-size-error" id="${inputId}-error" data-model-error="${fieldName}"></span>
    </div>`;
  }
  function scaleFieldsMarkup(instanceKey, initialRatio) {
    return SCALE_UNITS.map((unit) => `
    <div class="htbi-size-scale-row" data-scale-row="${unit}">
      <div class="htbi-size-scale-unit">${UNIT_LABELS[unit]}</div>
      ${SCALE_SIDES.map((side) => {
      const id = `${instanceKey}-scale-${side}-${unit}`;
      const sideLabel = side === "original" ? UI_TEXT.originalSize : UI_TEXT.scaledSize;
      const sourceLabel = side === "original" ? UI_TEXT.sourceOriginal : UI_TEXT.sourceScaled;
      return `
          <div class="htbi-size-field htbi-size-scale-cell" data-scale-cell="${side}-${unit}">
            <div class="htbi-size-label-line htbi-size-scale-mobile-label">
              <label class="htbi-size-label" for="${id}">${UI_TEXT.scaleFieldLabel(sideLabel, `<span data-scale-mobile-ratio="${side}">${side === "original" ? UI_TEXT.originalRatio : initialRatio}</span>`, UNIT_LABELS[unit])}</label>
            </div>
            <span class="htbi-size-source-badge" data-scale-source-badge hidden>${UI_TEXT.scaleSourceLabel(sourceLabel, UNIT_LABELS[unit])}</span>
            <input class="htbi-size-input" id="${id}" type="text" inputmode="decimal" autocomplete="off" data-scale-input data-scale-side="${side}" data-scale-unit="${unit}" aria-describedby="${id}-error">
            <span class="htbi-size-error" id="${id}-error" data-scale-source-error="${side}-${unit}"></span>
          </div>`;
    }).join("")}
    </div>`).join("");
  }
  function calculatorMarkup(instanceKey) {
    const initial = createInitialState();
    const initialRatio = formatScaleRatio(initial.scale.lastValidDenominator);
    const ratioListId = `${instanceKey}-scale-ratios`;
    return `
    <section class="htbi-size-shell" aria-labelledby="${instanceKey}-title">
      <header class="htbi-size-header">
        <p class="htbi-size-eyebrow">${UI_TEXT.eyebrow}</p>
        <h1 class="htbi-size-title" id="${instanceKey}-title">${UI_TEXT.title}</h1>
        <p class="htbi-size-introduction">${UI_TEXT.introduction}</p>
      </header>

      <div class="htbi-size-mode-selector" aria-label="${UI_TEXT.modeSelectorLabel}">
        <button class="htbi-size-mode-button" type="button" data-mode="quick" aria-pressed="true">${UI_TEXT.quickMode}</button>
        <button class="htbi-size-mode-button" type="button" data-mode="model" aria-pressed="false">${UI_TEXT.modelMode}</button>
        <button class="htbi-size-mode-button" type="button" data-mode="scale" aria-pressed="false">${UI_TEXT.scaleMode}</button>
      </div>

      <section class="htbi-size-panel" data-panel="quick" aria-labelledby="${instanceKey}-quick-heading">
        <div class="htbi-size-panel-heading-row">
          <div>
            <h2 class="htbi-size-section-title" id="${instanceKey}-quick-heading">${UI_TEXT.quickMode}</h2>
            <p class="htbi-size-section-copy">${UI_TEXT.quickInstructions}</p>
          </div>
          <button class="htbi-size-secondary-button" type="button" data-clear-quick>${UI_TEXT.clear}</button>
        </div>
        <div class="htbi-size-unit-groups">${quickFieldsMarkup(instanceKey)}</div>
      </section>

      <section class="htbi-size-panel" data-panel="model" aria-labelledby="${instanceKey}-model-heading" hidden>
        <h2 class="htbi-size-section-title" id="${instanceKey}-model-heading">${UI_TEXT.modelMode}</h2>
        <p class="htbi-size-section-copy">${UI_TEXT.modelIntroduction}</p>
        <div class="htbi-size-dimension-grid">
          ${modelFieldMarkup(instanceKey, "length", UI_TEXT.modelFields.length, initial.model.fields.length.unit)}
          ${modelFieldMarkup(instanceKey, "width", UI_TEXT.modelFields.width, initial.model.fields.width.unit)}
          ${modelFieldMarkup(instanceKey, "height", UI_TEXT.modelFields.height, initial.model.fields.height.unit)}
        </div>
        <div class="htbi-size-model-actions">
          <button class="htbi-size-primary-button" type="button" data-copy-results disabled>${UI_TEXT.copyResults}</button>
          <span class="htbi-size-copy-status" data-copy-status role="status" aria-live="polite"></span>
        </div>
        <div class="htbi-size-results" data-model-results hidden></div>
      </section>

      <section class="htbi-size-panel" data-panel="scale" aria-labelledby="${instanceKey}-scale-heading" hidden>
        <div class="htbi-size-panel-heading-row">
          <div>
            <h2 class="htbi-size-section-title" id="${instanceKey}-scale-heading">${UI_TEXT.scaleMode}</h2>
            <p class="htbi-size-section-copy">${UI_TEXT.scaleIntroduction}</p>
          </div>
          <button class="htbi-size-secondary-button" type="button" data-clear-scale>${UI_TEXT.clear}</button>
        </div>
        <div class="htbi-size-scale-control htbi-size-field">
          <label class="htbi-size-label" for="${instanceKey}-scale-denominator">${UI_TEXT.selectedScale}</label>
          <div class="htbi-size-ratio-input">
            <span aria-hidden="true">${UI_TEXT.ratioPrefix}</span>
            <input class="htbi-size-input" id="${instanceKey}-scale-denominator" type="text" inputmode="decimal" autocomplete="off" value="${initial.scale.rawDenominator}" list="${ratioListId}" data-scale-denominator aria-describedby="${instanceKey}-scale-denominator-error">
            <datalist id="${ratioListId}">${SCALE_PRESETS.map((value) => `<option value="${value}"></option>`).join("")}</datalist>
          </div>
          <span class="htbi-size-error" id="${instanceKey}-scale-denominator-error" data-scale-denominator-error></span>
        </div>
        <div class="htbi-size-scale-comparison" role="group" aria-label="${UI_TEXT.scaleComparisonLabel}">
          <div class="htbi-size-scale-headings" aria-hidden="true">
            <span>${UI_TEXT.scaleUnitHeading}</span>
            <strong>${UI_TEXT.originalSize} <span>${UI_TEXT.originalRatio}</span></strong>
            <strong>${UI_TEXT.scaledSize} <span data-scaled-ratio>${initialRatio}</span></strong>
          </div>
          ${scaleFieldsMarkup(instanceKey, initialRatio)}
        </div>
        <p class="htbi-size-scale-note">${UI_TEXT.scaleExplanation}</p>
      </section>

      <section class="htbi-size-reference" aria-labelledby="${instanceKey}-reference-heading">
        <h2 class="htbi-size-section-title" id="${instanceKey}-reference-heading">${UI_TEXT.referenceTitle}</h2>
        <ul class="htbi-size-reference-list">${UI_TEXT.referenceItems.map(({ label, value }) => `
          <li class="htbi-size-reference-item"><strong>${label}</strong><span>${value}</span></li>`).join("")}
        </ul>
      </section>

      <section class="htbi-size-explanation" aria-labelledby="${instanceKey}-how-heading">
        <h2 class="htbi-size-section-title" id="${instanceKey}-how-heading">${UI_TEXT.howTitle}</h2>
        <p class="htbi-size-section-copy">${UI_TEXT.howText}</p>
      </section>

      <aside class="htbi-size-disclaimer">
        <p>${UI_TEXT.measurementDisclaimer}</p>
        <p>${UI_TEXT.fanDisclaimer}</p>
      </aside>
      <span class="htbi-size-visually-hidden" data-announcement role="status" aria-live="polite" aria-atomic="true"></span>
    </section>`;
  }
  function validationMessage(code) {
    return UI_TEXT.errors[code] ?? UI_TEXT.errors.malformed;
  }
  function modelResultsMarkup(result) {
    const buildable = result.height.buildable;
    const length = result.closestStuds.length;
    const width = result.closestStuds.width;
    const grid = result.baseplateGrid;
    const labels = UI_TEXT.resultLabels;
    return `
    <section class="htbi-size-result-group htbi-size-result-target">
      <h3 class="htbi-size-results-title">${UI_TEXT.targetSize}</h3>
      <dl class="htbi-size-results-list">
        <div class="htbi-size-result-row"><dt>${labels.targetDimensions}</dt><dd>${formatDimensions(result.centimeters, UI_TEXT.units.centimeter)}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.imperialDimensions}</dt><dd>${formatDimensions(result.inches, UI_TEXT.units.inch)}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.exactFootprint}</dt><dd>${formatNumber(result.studFootprint.length)} \xD7 ${formatNumber(result.studFootprint.width)} ${UI_TEXT.units.studs}</dd></div>
      </dl>
    </section>
    <section class="htbi-size-result-group htbi-size-result-primary">
      <h3 class="htbi-size-results-title">${UI_TEXT.closestBuildableSize}</h3>
      <dl class="htbi-size-results-list">
        <div class="htbi-size-result-row"><dt>${labels.recommendedFootprint}</dt><dd>${length.roundedStuds} \xD7 ${width.roundedStuds} ${UI_TEXT.units.studs}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.actualFootprint}</dt><dd>${formatNumber(fromMillimeters(length.actualMillimeters, "centimeter"))} \xD7 ${formatNumber(fromMillimeters(width.actualMillimeters, "centimeter"))} ${UI_TEXT.units.centimeter}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.footprintDifference}</dt><dd>${UI_TEXT.modelFields.length}: ${formatSignedDifference(length.differenceMillimeters)}<br>${UI_TEXT.modelFields.width}: ${formatSignedDifference(width.differenceMillimeters)}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.buildableHeight}</dt><dd>${formatBuildableHeight(buildable.bricks, buildable.remainingPlates)}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.actualHeight}</dt><dd>${formatNumber(fromMillimeters(buildable.actualMillimeters, "centimeter"))} ${UI_TEXT.units.centimeter}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.heightDifference}</dt><dd>${formatSignedDifference(buildable.differenceMillimeters)}</dd></div>
      </dl>
    </section>
    <section class="htbi-size-result-group htbi-size-result-baseplates">
      <h3 class="htbi-size-results-title">${UI_TEXT.baseplateCoverage}</h3>
      <p class="htbi-size-baseplate-direction"><strong>${grid.lengthBaseplates} ${UI_TEXT.lengthDirection} \xD7 ${grid.widthBaseplates} ${UI_TEXT.widthDirection}</strong></p>
      <p class="htbi-size-baseplate-total">${formatUnitValue(grid.total, "baseplate32")} ${UI_TEXT.totalSuffix}</p>
      <p>${UI_TEXT.coverageArea}: ${formatNumber(grid.coveredLengthCentimeters)} \xD7 ${formatNumber(grid.coveredWidthCentimeters)} ${UI_TEXT.units.centimeter}</p>
      <p class="htbi-size-coverage-note">${UI_TEXT.coverageExplanation}</p>
    </section>
    <details class="htbi-size-more-details">
      <summary>${UI_TEXT.moreDetails}</summary>
      <dl class="htbi-size-results-list">
        <div class="htbi-size-result-row"><dt>${labels.exactHeightBricks}</dt><dd>${formatUnitValue(result.height.exactBricks, "brick")}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.exactHeightPlates}</dt><dd>${formatUnitValue(result.height.exactPlates, "plate")}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.roundedWholePlates}</dt><dd>${formatUnitValue(buildable.roundedPlates, "plate")}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.actualRoundedLength}</dt><dd>${formatUnitValue(length.actualMillimeters, "millimeter")}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.actualRoundedWidth}</dt><dd>${formatUnitValue(width.actualMillimeters, "millimeter")}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.lengthDifference}</dt><dd>${formatSignedDifference(length.differenceMillimeters)}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.widthDifference}</dt><dd>${formatSignedDifference(width.differenceMillimeters)}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.coveredLength}</dt><dd>${formatUnitValue(grid.coveredLengthStuds, "stud")} / ${formatUnitValue(grid.coveredLengthMillimeters, "millimeter")}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.coveredWidth}</dt><dd>${formatUnitValue(grid.coveredWidthStuds, "stud")} / ${formatUnitValue(grid.coveredWidthMillimeters, "millimeter")}</dd></div>
        <div class="htbi-size-result-row"><dt>${labels.technicalBaseplateGrid}</dt><dd>${grid.columns} \xD7 ${grid.rows}</dd></div>
      </dl>
    </details>`;
  }
  function createCalculator(root) {
    if (!(root instanceof Element)) throw new TypeError("Calculator root must be an Element.");
    if (root.dataset.htbiSizeInitialized === "true") return null;
    root.dataset.htbiSizeInitialized = "true";
    root.classList.add("htbi-size-root");
    root.innerHTML = calculatorMarkup(createInstanceKey());
    let state = createInitialState();
    const touchedModelFields = /* @__PURE__ */ new Set();
    const quickInputs = [...root.querySelectorAll("[data-quick-unit]")];
    const scaleInputs = [...root.querySelectorAll("[data-scale-input]")];
    const modeButtons = [...root.querySelectorAll("[data-mode]")];
    const panels = [...root.querySelectorAll("[data-panel]")];
    const announcement = root.querySelector("[data-announcement]");
    const results = root.querySelector("[data-model-results]");
    const copyButton = root.querySelector("[data-copy-results]");
    const copyStatus = root.querySelector("[data-copy-status]");
    const denominatorInput = root.querySelector("[data-scale-denominator]");
    const denominatorError = root.querySelector("[data-scale-denominator-error]");
    const announcementController = createAnnouncementController({
      write: (message) => {
        announcement.textContent = message;
      }
    });
    const copyStatusController = createCopyStatusController({
      onChange: (status) => {
        state = withCopyStatus(state, status);
        copyStatus.textContent = status === "success" ? UI_TEXT.copySuccess : status === "error" ? UI_TEXT.copyError : "";
      }
    });
    function renderMode() {
      modeButtons.forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.mode === state.mode)));
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.panel !== state.mode;
      });
    }
    function clearQuickDisplay() {
      quickInputs.forEach((input) => {
        input.value = "";
        input.removeAttribute("aria-invalid");
        const row = input.closest("[data-quick-row]");
        row.classList.remove("htbi-size-is-source", "htbi-size-has-error");
        row.querySelector("[data-source-badge]").hidden = true;
        row.querySelector("[data-quick-error]").textContent = "";
      });
    }
    function renderQuick(sourceInput, parsed) {
      quickInputs.forEach((input) => {
        const unit = input.dataset.quickUnit;
        const row = input.closest("[data-quick-row]");
        const isSource = input === sourceInput;
        row.classList.toggle("htbi-size-is-source", isSource);
        row.querySelector("[data-source-badge]").hidden = !isSource;
        row.classList.toggle("htbi-size-has-error", isSource && !parsed.valid);
        input.setAttribute("aria-invalid", String(isSource && !parsed.valid));
        row.querySelector("[data-quick-error]").textContent = isSource && !parsed.valid ? validationMessage(parsed.code) : "";
        if (!isSource) input.value = parsed.valid ? formatNumber(state.quick.values[unit]) : "";
      });
    }
    function handleQuickInput(event) {
      const input = event.currentTarget;
      const rawValue = input.value;
      if (rawValue.trim() === "") {
        announcementController.cancel();
        state = clearQuick(state);
        clearQuickDisplay();
        return;
      }
      const parsed = parseMeasurement(rawValue);
      if (!parsed.valid) {
        announcementController.cancel();
        state = withQuickSource(state, { sourceUnit: input.dataset.quickUnit, rawValue, canonicalMillimeters: null, values: {}, error: parsed.code });
        renderQuick(input, parsed);
        return;
      }
      const sourceUnit = input.dataset.quickUnit;
      const calculation = calculateQuickStatus(parsed.value, sourceUnit);
      if (!calculation.valid) {
        announcementController.cancel();
        state = withQuickSource(state, { sourceUnit, rawValue, canonicalMillimeters: null, values: {}, error: calculation.error });
        renderQuick(input, { ...parsed, valid: false, code: calculation.error });
        return;
      }
      state = withQuickSource(state, {
        sourceUnit,
        rawValue,
        canonicalMillimeters: calculation.value.canonicalMillimeters,
        values: calculation.value.values,
        error: null
      });
      renderQuick(input, parsed);
      announcementController.schedule(UI_TEXT.announcements.convertedFrom(UNIT_LABELS[sourceUnit]));
    }
    function updateModel(changedFieldName) {
      copyStatusController.clear();
      const parsedFields = {};
      let valid = true;
      for (const fieldName of MODEL_FIELDS) {
        const input = root.querySelector(`[data-model-field="${fieldName}"]`);
        const error = root.querySelector(`[data-model-error="${fieldName}"]`);
        const parsed = parseMeasurement(input.value);
        parsedFields[fieldName] = parsed;
        const showError = touchedModelFields.has(fieldName) && !parsed.valid;
        input.setAttribute("aria-invalid", String(showError));
        input.closest(".htbi-size-field").classList.toggle("htbi-size-has-error", showError);
        error.textContent = showError ? validationMessage(parsed.code) : "";
        state = withModelField(state, fieldName, {
          rawValue: input.value,
          unit: root.querySelector(`[data-model-unit="${fieldName}"]`).value,
          error: showError ? parsed.code : null
        });
        valid && (valid = parsed.valid);
      }
      if (!valid) {
        announcementController.cancel();
        state = withModelResult(state, null);
        results.hidden = true;
        copyButton.disabled = true;
        return;
      }
      const calculationInput = Object.fromEntries(MODEL_FIELDS.map((fieldName) => [fieldName, {
        value: parsedFields[fieldName].value,
        unit: state.model.fields[fieldName].unit
      }]));
      const calculation = calculateModelStatus(calculationInput);
      if (!calculation.valid) {
        announcementController.cancel();
        const fieldName = changedFieldName ?? MODEL_FIELDS.at(-1);
        const input = root.querySelector(`[data-model-field="${fieldName}"]`);
        const error = root.querySelector(`[data-model-error="${fieldName}"]`);
        state = withModelField(state, fieldName, { error: calculation.error });
        input.setAttribute("aria-invalid", "true");
        input.closest(".htbi-size-field").classList.add("htbi-size-has-error");
        error.textContent = validationMessage(calculation.error);
        state = withModelResult(state, null);
        results.hidden = true;
        copyButton.disabled = true;
        return;
      }
      const modelResult = calculation.value;
      state = withModelResult(state, modelResult);
      results.innerHTML = modelResultsMarkup(modelResult);
      results.hidden = false;
      copyButton.disabled = false;
      announcementController.schedule(UI_TEXT.announcements.modelUpdated);
    }
    function renderScale() {
      const activeKey = `${state.scale.activeSourceSide}-${state.scale.activeSourceUnit}`;
      const hasValues = state.scale.denominatorError === null && state.scale.sourceError === null && state.scale.activeSourceSide !== null;
      const ratio = formatScaleRatio(state.scale.lastValidDenominator);
      root.querySelectorAll("[data-scaled-ratio]").forEach((element) => {
        element.textContent = ratio;
      });
      root.querySelectorAll('[data-scale-mobile-ratio="scaled"]').forEach((element) => {
        element.textContent = ratio;
      });
      denominatorInput.setAttribute("aria-invalid", String(state.scale.denominatorError !== null));
      denominatorInput.closest(".htbi-size-field").classList.toggle("htbi-size-has-error", state.scale.denominatorError !== null);
      denominatorError.textContent = state.scale.denominatorError ? validationMessage(state.scale.denominatorError) : "";
      scaleInputs.forEach((input) => {
        const key = `${input.dataset.scaleSide}-${input.dataset.scaleUnit}`;
        const cell = input.closest("[data-scale-cell]");
        const isSource = key === activeKey;
        cell.classList.toggle("htbi-size-is-source", isSource);
        cell.classList.toggle("htbi-size-has-error", isSource && state.scale.sourceError !== null);
        cell.querySelector("[data-scale-source-badge]").hidden = !isSource;
        input.setAttribute("aria-invalid", String(isSource && state.scale.sourceError !== null));
        cell.querySelector("[data-scale-source-error]").textContent = isSource && state.scale.sourceError ? validationMessage(state.scale.sourceError) : "";
        if (!isSource) {
          input.value = hasValues ? formatNumber(state.scale.values[input.dataset.scaleSide][input.dataset.scaleUnit]) : "";
        }
      });
    }
    function applyScalePair(parsed, sourceSide, sourceUnit, denominator) {
      const calculation = calculateScaleStatus({ value: parsed.value, unit: sourceUnit, sourceSide, denominator });
      if (!calculation.valid) {
        state = withScaleState(state, {
          canonicalOriginalMillimeters: null,
          canonicalScaledMillimeters: null,
          values: { original: {}, scaled: {} },
          sourceError: calculation.error
        });
        return false;
      }
      const pair = calculation.value;
      state = withScaleState(state, {
        canonicalOriginalMillimeters: pair.originalMillimeters,
        canonicalScaledMillimeters: pair.scaledMillimeters,
        values: { original: pair.originalValues, scaled: pair.scaledValues },
        sourceError: null,
        denominatorError: null
      });
      return true;
    }
    function handleScaleInput(event) {
      const input = event.currentTarget;
      const sourceSide = input.dataset.scaleSide;
      const sourceUnit = input.dataset.scaleUnit;
      const parsed = parseMeasurement(input.value);
      state = withScaleState(state, {
        activeSourceSide: sourceSide,
        activeSourceUnit: sourceUnit,
        rawSourceValue: input.value,
        sourceError: parsed.valid ? null : parsed.code,
        values: { original: {}, scaled: {} },
        canonicalOriginalMillimeters: null,
        canonicalScaledMillimeters: null
      });
      const ratio = validateScaleDenominator(state.scale.rawDenominator);
      if (parsed.valid) {
        if (ratio.valid) applyScalePair(parsed, sourceSide, sourceUnit, ratio.denominator);
      }
      renderScale();
      if (parsed.valid && ratio.valid && state.scale.sourceError === null) {
        announcementController.schedule(UI_TEXT.announcements.scaleUpdated(formatScaleRatio(ratio.denominator)));
      } else {
        announcementController.cancel();
      }
    }
    function handleDenominatorInput() {
      const rawDenominator = denominatorInput.value;
      const ratio = validateScaleDenominator(rawDenominator);
      state = withScaleState(state, {
        rawDenominator,
        denominatorError: ratio.valid ? null : ratio.code,
        values: ratio.valid ? state.scale.values : { original: {}, scaled: {} },
        canonicalOriginalMillimeters: ratio.valid ? state.scale.canonicalOriginalMillimeters : state.scale.canonicalOriginalMillimeters,
        canonicalScaledMillimeters: ratio.valid ? state.scale.canonicalScaledMillimeters : state.scale.canonicalScaledMillimeters,
        ...ratio.valid ? { lastValidDenominator: ratio.denominator } : {}
      });
      const sourceParsed = parseMeasurement(state.scale.rawSourceValue);
      if (ratio.valid && state.scale.activeSourceSide && sourceParsed.valid) {
        applyScalePair(sourceParsed, state.scale.activeSourceSide, state.scale.activeSourceUnit, ratio.denominator);
      }
      renderScale();
      if (ratio.valid && state.scale.activeSourceSide && sourceParsed.valid && state.scale.sourceError === null) {
        announcementController.schedule(UI_TEXT.announcements.scaleUpdated(formatScaleRatio(ratio.denominator)));
      } else {
        announcementController.cancel();
      }
    }
    modeButtons.forEach((button) => button.addEventListener("click", () => {
      announcementController.cancel();
      copyStatusController.clear();
      state = withMode(state, button.dataset.mode);
      renderMode();
    }));
    quickInputs.forEach((input) => input.addEventListener("input", handleQuickInput));
    root.querySelector("[data-clear-quick]").addEventListener("click", () => {
      state = clearQuick(state);
      clearQuickDisplay();
      announcementController.replace(UI_TEXT.announcements.quickCleared);
    });
    root.querySelectorAll("[data-model-field]").forEach((input) => input.addEventListener("input", () => {
      touchedModelFields.add(input.dataset.modelField);
      updateModel(input.dataset.modelField);
    }));
    root.querySelectorAll("[data-model-unit]").forEach((select) => select.addEventListener("change", () => {
      touchedModelFields.add(select.dataset.modelUnit);
      updateModel(select.dataset.modelUnit);
    }));
    scaleInputs.forEach((input) => input.addEventListener("input", handleScaleInput));
    denominatorInput.addEventListener("input", handleDenominatorInput);
    root.querySelector("[data-clear-scale]").addEventListener("click", () => {
      state = clearScale(state);
      denominatorInput.value = state.scale.rawDenominator;
      scaleInputs.forEach((input) => {
        input.value = "";
      });
      renderScale();
      announcementController.replace(UI_TEXT.announcements.scaleCleared);
    });
    copyButton.addEventListener("click", async () => {
      if (!state.model.result) return;
      const calculationInput = Object.fromEntries(MODEL_FIELDS.map((fieldName) => [fieldName, {
        value: parseMeasurement(state.model.fields[fieldName].rawValue).value,
        unit: state.model.fields[fieldName].unit
      }]));
      try {
        if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
        await navigator.clipboard.writeText(createCopySummary(calculationInput, state.model.result));
        copyStatusController.set("success");
      } catch {
        copyStatusController.set("error");
      }
    });
    renderMode();
    renderScale();
    return Object.freeze({ root, getState: () => structuredClone(state) });
  }

  // src/ui/main.js
  function mountSizeCalculators(scope = document) {
    return [...scope.querySelectorAll("[data-htbi-size-calculator]")].map((root) => createCalculator(root)).filter(Boolean);
  }
  function initialize() {
    mountSizeCalculators();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
